/**
 * What a layer was made of, so it can be made of it differently.
 *
 * A fill is pixels the moment it lands: `applyFabric` renders the tile into the
 * layer's canvas and nothing survives about *what it was*. The layer is called
 * "Gingham Check" and that name is the only trace, so changing your mind means
 * undo and start again.
 *
 * This is the record that makes a fill **a setting rather than an act**. With it
 * the colour becomes editable after the fact, and so does the weave size, and
 * "the same design in another colourway" stops being a feature and becomes a
 * loop over one small object.
 *
 * ## It has to expire, and it expires by derivation
 *
 * If someone paints on a fabric layer and then re-tints, a naive re-fill
 * repaints the whole layer and their strokes are gone. That is the one outcome
 * this must never have.
 *
 * So the record stores the layer's `rev` at the moment the fill produced it.
 * `rev` already bumps on every pixel write, so a layer that has been painted on
 * since has a different one, and {@link fabricOf} returns null. The layer is no
 * longer "a fabric" — it is artwork that started as one.
 *
 * Nothing hooks the paint path to make that true. Comparing two numbers is
 * cheaper than maintaining an invariant, it cannot drift out of sync with the
 * pixels it describes, and it restores itself correctly on undo — which a
 * `delete layer.fabric` on first brush stroke would not.
 */
import type { FabricFill } from "../../shared/format";

/**
 * The durable half plus the change token.
 *
 * `FabricFill` (shared/format.ts) is what survives a save: which fabric, at
 * what size, in what colour. This adds the one field that must not — the rev
 * the fill produced, which only means anything inside the session that wrote
 * it. Splitting them is what keeps the wrong half from being written to disk.
 */
export interface FabricSource extends FabricFill {
  /**
   * The layer's `rev` immediately after the fill.
   *
   * Not a version and never incremented here — only ever compared. See the
   * expiry note above.
   */
  rev: number;
}

/**
 * The minimum a layer has to look like for this module to reason about it.
 *
 * Structural rather than importing RuntimeLayer, so the rules below can be
 * tested in plain Node — vitest runs without a DOM, and RuntimeLayer carries an
 * HTMLCanvasElement.
 */
export interface FabricBearing {
  rev: number;
  fabric?: FabricSource;
}

/**
 * The record a fill should store.
 *
 * `rev` is passed rather than read, because the caller has to capture it *after*
 * `markPainted()` — which bumps it — and getting that order wrong would produce
 * a record that is stale the instant it is written.
 */
export function recordFabric(
  fabric: { id: string; name: string },
  tilePx: number,
  tint: string | null,
  revAfterFill: number,
): FabricSource {
  return { id: fabric.id, name: fabric.name, tilePx, tint, rev: revAfterFill };
}

/**
 * What this layer is still made of, or null if it is not made of it any more.
 *
 * Null in two cases and they mean the same thing to a caller: there was never a
 * fabric here, or there was and it has been painted over.
 */
export function fabricOf(layer: FabricBearing): FabricSource | null {
  const source = layer.fabric;
  if (!source) return null;
  return source.rev === layer.rev ? source : null;
}

/**
 * Put a record back on a layer whose pixels have just been restored.
 *
 * Undo and redo replace a layer's pixels, and a record describes *specific*
 * pixels — it carries the rev they had. So the record has to travel with them,
 * and it has to be re-stamped rather than restored verbatim: the write that
 * just happened bumped the rev, and a verbatim record would point at a rev the
 * layer no longer has, which is precisely the state that means "painted over".
 *
 * `undefined` means the edit says nothing about the record and it is left
 * alone — every brush stroke on every layer that was never a fabric. `null`
 * means the edit knows there was no fabric, and clears it.
 */
export function restoreFabric(layer: FabricBearing, record: FabricSource | null | undefined): void {
  if (record === undefined) return;
  if (record === null) delete layer.fabric;
  else layer.fabric = { ...record, rev: layer.rev };
}

/** Can this layer's fabric still be re-tinted or re-scaled without losing work? */
export function isRecolourable(layer: FabricBearing): boolean {
  return fabricOf(layer) !== null;
}

/**
 * The same fill with one thing changed.
 *
 * Returns a new record rather than mutating: the old one is still the truth
 * until the re-fill has actually happened and produced a new `rev`.
 */
export function withTint(source: FabricSource, tint: string | null): FabricSource {
  return { ...source, tint };
}

export function withTilePx(source: FabricSource, tilePx: number): FabricSource {
  return { ...source, tilePx };
}

/**
 * Whether re-filling with this change is worth doing at all.
 *
 * A picker streaming `onChange` can deliver the colour it already has many
 * times over a drag; re-rendering the fabric for each is work nobody asked for.
 */
export function differs(source: FabricSource, next: FabricSource): boolean {
  return source.id !== next.id || source.tint !== next.tint || source.tilePx !== next.tilePx;
}
